import { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Spinner,
  Text,
  mergeClasses,
  type AccordionToggleEventHandler,
} from '@coreai/fluentui-react';
import type { OperationDefinition } from '@/types/operations';
import type { ConnectorManifest } from '@/types/connections';
import { useOperationsByConnector } from '@/hooks/useOperations';
import { ConnectorBrandIcon } from './ConnectorBrandIcon';
import { ConnectorFavoriteButton } from './ConnectorFavoriteButton';
import { ConnectorOperationsList } from './ConnectorOperationsList';
import { useConnectorSectionStyles } from './ConnectorSection.styles';

type AzureConnectorSectionProps = {
  connector: ConnectorManifest;
  actionType?: 'triggers' | 'actions';
  onOperationSelect: (operation: OperationDefinition, event?: React.MouseEvent) => void;
  defaultExpanded?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  noMargin?: boolean;
  /** Reports total operation count once the lazy fetch resolves. */
  onOperationsLoaded?: (count: number) => void;
  /** Reports the displayed count after internal filtering (for height calc). */
  onDisplayedCountChange?: (count: number, isSearching: boolean) => void;
  initialLimit?: number;
  showAll?: boolean;
  onShowAllChange?: (showAll: boolean) => void;
  headerTabIndex?: number;
  headerIndex?: number;
  /**
   * Whether the section can be collapsed. Defaults to `true`. When `false`
   * (e.g. the search drill-in view) the chevron is hidden and the header
   * loses its clickable affordance — the panel is always shown.
   */
  isCollapsible?: boolean;
};

export const AzureConnectorSection = ({
  connector,
  actionType,
  onOperationSelect,
  defaultExpanded = false,
  isExpanded: controlledExpanded,
  onExpandedChange,
  noMargin = false,
  onOperationsLoaded,
  onDisplayedCountChange,
  initialLimit,
  showAll = false,
  onShowAllChange,
  headerTabIndex,
  headerIndex,
  isCollapsible = true,
}: AzureConnectorSectionProps) => {
  const styles = useConnectorSectionStyles();
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  // When non-collapsible, force expanded regardless of incoming props
  // so the drill-in view always shows operations.
  const isExpanded = !isCollapsible ? true : (controlledExpanded ?? internalExpanded);
  const openItems = isExpanded ? [connector.id] : [];

  const { data: operations = [], isLoading } = useOperationsByConnector(
    isExpanded ? connector.id : null,
    actionType,
  );

  // The manifest may be synthesized from search results when the legacy
  // Azure groups hook hasn't hydrated this connector (or when the search
  // group's id doesn't match the hook's id format). In those cases the
  // brand color can be missing — fall back to a loaded operation's color
  // (each `OperationDefinition` carries `hexToColor`-normalized brand)
  // so the icon tile and operations rail stay visible.
  const effectiveBrandColor =
    connector.brandColor || operations[0]?.brandColor || '#0078D4';

  useEffect(() => {
    if (isExpanded && !isLoading && operations.length > 0) {
      onOperationsLoaded?.(operations.length);
    }
  }, [isExpanded, isLoading, operations.length, onOperationsLoaded]);

  const handleToggle: AccordionToggleEventHandler = useCallback(
    (_e, data) => {
      if (!isCollapsible) return;
      const next = data.openItems.includes(connector.id);
      setInternalExpanded(next);
      onExpandedChange?.(next);
    },
    [connector.id, isCollapsible, onExpandedChange],
  );

  return (
    <Accordion
      collapsible
      openItems={openItems}
      onToggle={handleToggle}
      className={mergeClasses(styles.container, noMargin && styles.containerNoMargin)}
    >
      <AccordionItem value={connector.id}>
        <AccordionHeader
          expandIconPosition="start"
          icon={
            <ConnectorBrandIcon brandColor={effectiveBrandColor} iconUri={connector.iconUri} />
          }
          // Hide the chevron when the section can't be collapsed —
          // showing it implies an interaction that does nothing.
          {...(!isCollapsible && { expandIcon: null })}
          className={mergeClasses(
            styles.header,
            isExpanded && styles.headerExpanded,
            !isCollapsible && styles.headerStatic,
          )}
          button={{
            'data-connector-header': true,
            'data-connector-index': headerIndex,
            tabIndex: headerTabIndex ?? 0,
            'aria-label': `${connector.displayName} connector`,
          } as Record<string, unknown>}
        >
          <div className={styles.connectorInfo}>
            <Text className={styles.connectorName}>{connector.displayName}</Text>
            {connector.description && (
              <Text className={styles.connectorDescription}>{connector.description}</Text>
            )}
          </div>
          <div className={styles.headerControls}>
            <ConnectorFavoriteButton
              connectorId={connector.id}
              connectorName={connector.displayName}
            />
            {isExpanded && !isLoading && operations.length > 0 && (
              <Badge className={styles.badge} appearance="tint" color="informative" size="small">
                {operations.length}
              </Badge>
            )}
          </div>
        </AccordionHeader>
        <AccordionPanel className={styles.panel}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <Spinner size="tiny" />
              <Text>Loading operations...</Text>
            </div>
          ) : operations.length === 0 ? (
            <div className={styles.emptyState}>
              <Text>
                {actionType
                  ? `${connector.displayName} has no ${actionType}.`
                  : 'No operations available'}
              </Text>
            </div>
          ) : (
            <ConnectorOperationsList
              operations={operations}
              onOperationSelect={onOperationSelect}
              brandColor={effectiveBrandColor}
              initialLimit={initialLimit}
              showAll={showAll}
              {...(onShowAllChange && { onShowAllChange })}
              isExpanded={isExpanded}
              {...(onDisplayedCountChange && { onDisplayedCountChange })}
            />
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default AzureConnectorSection;
